/**
 * 落地页组件。
 *
 * 区块叙事顺序（主张 → 证据 → 差异化 → 机制 → 受众 → 价格 → 异议）：
 * 1. Hero — 价值主张 + CTA（生产环境为构建期静态岛，React 仅 Portal 接管 CTA）
 * 2. 社区精选 3D 模型展示（PlazaShowcase，热门 8 条，可就地预览）— 主张之后的实证
 * 3. Why Tsuvi — 3 张能力卡（单图直出 / 白模可打印 / PBR 精度）
 * 4. How it works — 3 步流程（Upload → Generate → Download）
 * 5. Use cases — 6 受众入口
 * 6. 定价预览
 * 7. FAQ（配 FAQPage Schema，见 components/Seo）— 处理异议
 * 8. Footer
 *
 * 顶栏锚点快捷菜单按视觉顺序排列（社区/教程/定价/FAQ），平滑滚动到对应区块；
 * 博客不在首页设区块，顶栏「博客」直接路由跳转英文专属 /:lang/blog。
 */

import { useState, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  SignInButton,
  SignUpButton,
  UserButton,
  SignedIn,
  SignedOut,
  useUser,
} from "@clerk/clerk-react";
import PricingMatrix from "./components/PricingMatrix";
import LocaleSwitcher from "./components/LocaleSwitcher";
import PlazaShowcase from "./components/PlazaShowcase";
import Logo from "./components/Brand";
import { LocalizedLink, localizedPath, useLang } from "./lib/lang";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "./lib/support";
import { USER_BUTTON_POPOVER_CARD } from "./lib/clerkAppearance";
import { Link, useLocation, useNavigate, type Location } from "react-router-dom";
import type { BillingInterval } from "./lib/api";
import {
  Image,
  Gem,
  Star,
  StarFilled,
  CheckCircle,
  CreditCard,
  Download,
  Loader,
  Mail,
  Zap,
  Target,
  Wrench,
  Plus,
  Minus,
} from "./components/icons";

// ── Hero 静态岛 ──
// 构建期 prerender 为 4 语言落地页注入 #landing-static（导航槽 + 完整 Hero 静态 HTML，
// 位于 #root 外侧——createRoot 挂载时会清空 #root 重建 DOM，岛必须在其外才能存活）。
// LCP 元素（hero h1）随 CSS 即刻绘制，不再等 JS boot；React 只通过 Portal 接管
// 两个动态位：导航栏（#landing-nav-slot）与 CTA 按钮（#hero-cta-slot）。
// 静态 Hero 标记与下方 Hero 组件逐类名镜像，改动任一侧必须同步（见 scripts/prerender.mjs）。
const LANDING_STATIC_ID = "landing-static";
const NAV_SLOT_ID = "landing-nav-slot";
const HERO_CTA_SLOT_ID = "hero-cta-slot";

/** 取静态岛根节点（dev / 测试 / 非落地页预渲染产物中不存在 → 走常规渲染）。 */
function getLandingStatic(): HTMLElement | null {
  return typeof document === "undefined"
    ? null
    : document.getElementById(LANDING_STATIC_ID);
}

export default function LandingPage() {
  // 岛检测是纯 DOM 查询，渲染期恒定；岛存在时导航/CTA 走 Portal，Hero 本体不重复渲染
  const island = getLandingStatic();
  // 岛显隐：静态 HTML 初始可见（首屏 LCP）；SPA 离开落地页时隐藏，
  // 返回时恢复——React 不拥有这些节点，用 hidden 属性做挂载/卸载对称控制。
  useEffect(() => {
    if (!island) return;
    island.hidden = false;
    return () => {
      island.hidden = true;
    };
  }, [island]);

  const navbar = <Navbar />;
  return (
    <>
      {island
        ? createPortal(navbar, document.getElementById(NAV_SLOT_ID) ?? island)
        : navbar}
      {island && <HeroCtaPortal />}
      {/* main 地标：读屏软件按 landmark 导航，Lighthouse landmark-one-main 审计要求。
          岛模式下 Hero 在 main 外（静态岛），main 从 Showcase 开始。 */}
      <main>
        {!island && <Hero />}
        <PlazaShowcase />
        <PainPoints />
        <HowItWorks />
        <UseCases />
        <Pricing />
        <LandingFaq />
      </main>
      <Footer />
    </>
  );
}

// ── 导航栏（含认证控件）──

function Navbar() {
  const { t } = useTranslation();
  const lang = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  // 顶栏中段快捷菜单：锚点按区块视觉顺序排列，全部平滑滚动到首页对应区块；
  // 博客不在首页设区块，「博客」为 SPA 路由跳转（根级 /blog，英文独占路由，
  // 无语言前缀——与文章页内既有链接一致）。
  const NAV_LINKS = [
    { href: "#community", label: t("landing.nav.community") },
    { href: "#how", label: t("landing.nav.how") },
    { href: "#pricing", label: t("landing.nav.pricing") },
    { href: "/blog", label: t("nav.blog") },
    { href: "#faq", label: t("landing.nav.faq") },
  ] as const;

  /** 打开「我的订阅」路由态弹窗（与工作台侧边栏账号菜单一致）：
   * SPA 导航带 state.background，首页保持挂载，弹窗叠加其上（见 App.tsx）。 */
  function openBilling() {
    const state = location.state as { background?: Location } | null;
    navigate(`/${lang}/workbench/billing`, {
      state: { background: state?.background ?? location },
    });
  }
  return (
    <nav className="navbar">
      <Logo />
      <div className="nav-links">
        {NAV_LINKS.map((l) =>
          l.href.startsWith("#") ? (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ) : (
            <Link key={l.href} to={l.href}>
              {l.label}
            </Link>
          ),
        )}
      </div>
      <div className="nav-auth">
        <LocaleSwitcher />
        <SignedIn>
          <LocalizedLink to="/workbench/generate" className="nav-btn nav-generate">
            {t("common.workbench")}
          </LocalizedLink>
          {/* 菜单项与工作台侧边栏账号菜单一致（我的订阅 / 帮助中心）：
              一律用 UserButton.Action + SPA navigate，避免 Clerk Link 的
              原生 <a> 整页重载；帮助中心指向 FAQ（mailto 直链有缺陷，不用）。 */}
          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: {
                // 弹出菜单宽度与工作台侧边栏账号弹层一致
                userButtonPopoverCard: USER_BUTTON_POPOVER_CARD,
              },
            }}
          >
            <UserButton.MenuItems>
              <UserButton.Action
                label={t("nav.billing")}
                labelIcon={<CreditCard size={16} />}
                onClick={openBilling}
              />
              <UserButton.Action
                label={t("nav.helpCenter")}
                labelIcon={<Mail size={16} />}
                onClick={() => navigate(`/${lang}/faq`)}
              />
            </UserButton.MenuItems>
          </UserButton>
        </SignedIn>
        <SignedOut>
          {/* asChild：避免 Clerk 自身 button 与子 button 嵌套（非法 HTML，浏览器会弹开内层） */}
          <SignInButton mode="modal">
            <button className="nav-btn nav-signin">{t("common.signIn")}</button>
          </SignInButton>
          <SignUpButton mode="modal">
            <button className="nav-btn nav-signup">{t("common.signUp")}</button>
          </SignUpButton>
        </SignedOut>
      </div>
    </nav>
  );
}

// ── 1. Hero ──

/** Hero 的 CTA 区（登录态三分支）。全量渲染与静态岛 Portal 共用同一份逻辑。 */
function HeroCta() {
  const { t } = useTranslation();
  const lang = useLang();
  // Clerk 未就绪时 SignedIn/SignedOut 都不渲染 → CTA 按钮晚 ~1s 才出现，
  // 会把 Hero 下方全部内容推下去（staging 实测单次 CLS 0.09 的元凶）。
  // 用同尺寸隐藏占位（visibility 保留布局）直到 isLoaded，换真按钮零偏移。
  // 静态岛的占位按钮（prerender 注入）与此处 isLoaded=false 分支同构。
  const { isLoaded, isSignedIn } = useUser();
  if (!isLoaded) {
    return (
      <button
        className="cta-button"
        aria-hidden
        tabIndex={-1}
        disabled
        style={{ visibility: "hidden" }}
      >
        {t("landing.hero.ctaStart")}
      </button>
    );
  }
  if (isSignedIn) {
    return (
      <LocalizedLink to="/workbench/generate" className="cta-button">
        {t("landing.hero.cta")}
      </LocalizedLink>
    );
  }
  /* 未登录：主 CTA 引导注册后进入工作台 */
  return (
    <SignUpButton mode="modal" forceRedirectUrl={localizedPath("/workbench/generate", lang)}>
      <button className="cta-button">{t("landing.hero.ctaStart")}</button>
    </SignUpButton>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="hero">
      {/* 背景光斑 + 网格层（纯 CSS 装饰） */}
      <div className="hero-glow hero-glow-1" aria-hidden />
      <div className="hero-glow hero-glow-2" aria-hidden />
      <div className="hero-grid-bg" aria-hidden />
      <div className="hero-content">
        <h1 className="gradient-text">{t("landing.hero.title")}</h1>
        <p className="tagline">{t("landing.hero.tagline")}</p>
        <HeroCta />
      </div>
    </section>
  );
}

/**
 * 静态岛模式的 CTA 接管：把 HeroCta portal 进静态 #hero-cta-slot，
 * 并移除 prerender 注入的静态占位按钮（同尺寸同标记 → 替换零 CLS）。
 * 只认 data-static-placeholder 标记——SPA 返回落地页二次挂载时槽内已是
 * React 自己的 CTA，无差别删 button 会把 React 节点误删导致卸载崩溃。
 * h1/tagline 留在静态岛中不被 React 触碰——LCP 元素不重绘是整套方案的前提。
 */
function HeroCtaPortal() {
  const slot =
    typeof document === "undefined"
      ? null
      : document.getElementById(HERO_CTA_SLOT_ID);
  // useLayoutEffect（绘制前提交）：React CTA 与静态占位删除同帧生效。
  // 若用 useEffect，首帧会绘制「静态占位 + React CTA」双按钮（移动端 block
  // 布局纵向堆叠 ~60px 瞬态凸起，实测 CLS 0.15 的元凶），下一帧才塌回。
  useLayoutEffect(() => {
    if (!slot) return;
    slot.querySelector("button[data-static-placeholder]")?.remove();
  }, [slot]);
  if (!slot) return null;
  return createPortal(<HeroCta />, slot);
}

// ── 3. Why Tsuvi（3 张能力卡）──
// 导出格式与速度并入 How it works 下载步骤；商业授权由紧随其后的定价区覆盖。

const PAIN_POINT_KEYS = ["modeling", "printable", "commercial"] as const;

// 「Why Tsuvi?」3 张能力卡片图标（统一线性 stroke 风格）：
// 单图直出 · 白模干净 · 极致材质
const PAIN_POINT_ICONS: Record<
  (typeof PAIN_POINT_KEYS)[number],
  React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number }>
> = {
  modeling: Image, // 单图直出可用资产
  printable: Gem, // 白模干净锐利
  commercial: Star, // 行业顶尖材质纹理
};

function PainPoints() {
  const { t } = useTranslation();
  return (
    <section className="pain-points">
      <span className="eyebrow">{t("landing.sectionBadges.why")}</span>
      <h2 className="section-title">{t("landing.painPoints.title")}</h2>
      <div className="cards">
        {PAIN_POINT_KEYS.map((key) => {
          const Icon = PAIN_POINT_ICONS[key];
          return (
            <div key={key} className="card glow-card">
              <span className="card-icon" aria-hidden>
                <Icon size={24} />
              </span>
              <h3>{t(`landing.painPoints.items.${key}.pain`)}</h3>
              <p>{t(`landing.painPoints.items.${key}.solution`)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── 4. How it works（3 步流程）──
// 搜索引擎偏好 HowTo 结构；同时降低用户认知门槛。配 HowTo Schema 见 components/Seo。

const HOW_STEPS = ["upload", "generate", "download"] as const;

const HOW_STEP_ICONS: Record<
  (typeof HOW_STEPS)[number],
  React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number }>
> = {
  upload: Image,
  generate: Loader,
  download: Download,
};

function HowItWorks() {
  const { t } = useTranslation();
  return (
    <section id="how" className="how-it-works">
      <span className="eyebrow">{t("landing.sectionBadges.how")}</span>
      <h2 className="section-title">{t("landing.howItWorks.title")}</h2>
      <p className="section-subtitle">{t("landing.howItWorks.subtitle")}</p>
      <div className="how-steps">
        {HOW_STEPS.map((step) => {
          const Icon = HOW_STEP_ICONS[step];
          return (
            <div key={step} className="how-step">
              <span className="card-icon" aria-hidden>
                <Icon size={24} />
              </span>
              <h3>{t(`landing.howItWorks.steps.${step}.title`)}</h3>
              <p>{t(`landing.howItWorks.steps.${step}.body`)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── 5. Use cases（6 受众入口）──
// P0 阶段为纯展示卡片（链接到 /use-cases/* 留待 P1 页面建成）。
// miniatures 已并入 tabletop 文案；art 裁撤（education 在 SEO 内容计划内保留）。

const USE_CASE_KEYS = [
  "printing",
  "tabletop",
  "gameDev",
  "education",
  "product",
  "jewelry",
] as const;

const USE_CASE_ICONS: Record<
  (typeof USE_CASE_KEYS)[number],
  React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number }>
> = {
  printing: CheckCircle,
  tabletop: Target,
  gameDev: Zap,
  education: Gem,
  product: Wrench,
  jewelry: StarFilled,
};

function UseCases() {
  const { t } = useTranslation();
  return (
    <section className="use-cases">
      <span className="eyebrow">{t("landing.sectionBadges.useCases")}</span>
      <h2 className="section-title">{t("landing.useCases.title")}</h2>
      <p className="section-subtitle">{t("landing.useCases.subtitle")}</p>
      <div className="cards">
        {USE_CASE_KEYS.map((key) => {
          const Icon = USE_CASE_ICONS[key];
          return (
            <div key={key} className="card">
              <span className="card-icon" aria-hidden>
                <Icon size={24} />
              </span>
              <h3>{t(`landing.useCases.items.${key}.title`)}</h3>
              <p>{t(`landing.useCases.items.${key}.body`)}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── 6. 定价 ──
// 复用 PricingMatrix（与 /pricing 页一致），已登录可直接订阅走 Creem。

function Pricing() {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<BillingInterval>("month");
  return (
    <section id="pricing" className="pricing landing-pricing">
      <span className="eyebrow">{t("landing.sectionBadges.pricing")}</span>
      <h2 className="section-title">{t("landing.pricing.title")}</h2>
      <div className="interval-toggle">
        <button
          className={interval === "month" ? "active" : ""}
          onClick={() => setInterval("month")}
        >
          {t("pricing.interval.month")}
        </button>
        <button
          className={interval === "year" ? "active" : ""}
          onClick={() => setInterval("year")}
        >
          {t("pricing.interval.year")}
          <span className="save-badge">{t("pricing.save2")}</span>
        </button>
      </div>
      <PricingMatrix interval={interval} />
    </section>
  );
}

// ── 7. FAQ（软性，配 FAQPage Schema）──

const FAQ_KEYS = ["free", "firstMonth", "formats", "printReady", "commercial", "time"] as const;

function LandingFaq() {
  const { t } = useTranslation();
  return (
    <section id="faq" className="landing-faq">
      <span className="eyebrow">{t("landing.sectionBadges.faq")}</span>
      <h2 className="section-title">{t("landing.faq.title")}</h2>
      {/* 客服联系行（Creem MoR 合规：公开页面可见客服邮箱；与 FAQ 页副标题一致） */}
      <p className="section-subtitle">
        {t("faq.subtitleContact")}{" "}
        <a href={SUPPORT_MAILTO}>{SUPPORT_EMAIL}</a>
      </p>
      {FAQ_KEYS.map((key) => (
        <details key={key}>
          <summary>
            <span>{t(`landing.faq.items.${key}.q`)}</span>
            <Plus size={16} className="faq-toggle-icon faq-toggle-plus" />
            <Minus size={16} className="faq-toggle-icon faq-toggle-minus" />
          </summary>
          <p>{t(`landing.faq.items.${key}.a`)}</p>
        </details>
      ))}
      <div className="landing-faq-more">
        <LocalizedLink to="/faq">{t("landing.faq.browseAll")}</LocalizedLink>
      </div>
    </section>
  );
}

// ── 8. Footer ──

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="footer">
      {/* 左侧品牌区：Logo + 一句话定位 + 版权 */}
      <div className="footer-brand">
        <Logo />
        <p className="footer-tagline">{t("landing.footer.tagline")}</p>
        <p className="footer-copy">
          {t("landing.footer.copy", { year: new Date().getFullYear() })}
        </p>
      </div>
      {/* 右侧链接区：按「产品 / 法律」分组，组内纵向排列。
          外部链接（社交/联系）将来作为独立分组加入。 */}
      <div className="footer-cols">
        <nav aria-label={t("landing.footerLinks.aria")}>
          <p className="footer-col-title">{t("landing.footer.groupProduct")}</p>
          <div className="footer-links">
            <LocalizedLink to="/plaza">{t("landing.footerLinks.community")}</LocalizedLink>
            <LocalizedLink to="/pricing">{t("landing.footerLinks.pricing")}</LocalizedLink>
            <LocalizedLink to="/faq">{t("landing.footerLinks.faq")}</LocalizedLink>
          </div>
        </nav>
        <nav>
          <p className="footer-col-title">{t("landing.footer.groupLegal")}</p>
          <div className="footer-links">
            <LocalizedLink to="/terms">{t("landing.footerLinks.terms")}</LocalizedLink>
            <LocalizedLink to="/privacy">{t("landing.footerLinks.privacy")}</LocalizedLink>
            <LocalizedLink to="/aup">{t("landing.footerLinks.aup")}</LocalizedLink>
          </div>
        </nav>
      </div>
    </footer>
  );
}
